import React, { useState, useRef, useEffect } from 'react';

/**
 * A digit-by-digit passcode input. Auto-advances focus as user types.
 * Length is variable (4-6 digits based on what the user originally set).
 * Supports paste of the full code.
 */
export default function PasscodeInput({ length = 6, value, onChange, disabled = false, autoFocus = true }) {
  const [digits, setDigits] = useState(() => Array.from({ length }, (_, i) => value?.[i] || ''));
  const refs = useRef([]);

  // Sync when parent resets `value` to empty string
  useEffect(() => {
    if (!value) setDigits(Array.from({ length }, () => ''));
  }, [value, length]);

  const commit = (next) => {
    setDigits(next);
    onChange(next.join(''));
  };

  const handleChange = (i, e) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (!raw) {
      const next = [...digits];
      next[i] = '';
      commit(next);
      return;
    }
    // If user pasted/typed multiple digits, distribute them forward
    const chars = raw.split('');
    const next = [...digits];
    for (let j = 0; j < chars.length && i + j < length; j++) {
      next[i + j] = chars[j];
    }
    commit(next);
    const focusIdx = Math.min(i + chars.length, length - 1);
    refs.current[focusIdx]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      refs.current[i - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && i > 0) refs.current[i - 1]?.focus();
    if (e.key === 'ArrowRight' && i < length - 1) refs.current[i + 1]?.focus();
  };

  const handlePaste = (i, e) => {
    const text = (e.clipboardData || window.clipboardData)?.getData('text') || '';
    const clean = text.replace(/\D/g, '').slice(0, length);
    if (!clean) return;
    e.preventDefault();
    const next = Array.from({ length }, (_, k) => clean[k] || '');
    commit(next);
    refs.current[Math.min(clean.length, length - 1)]?.focus();
  };

  return (
    <div className="flex justify-center gap-2 sm:gap-3" data-testid="passcode-input">
      {Array.from({ length }).map((_, i) => (
        <input
          key={`slot-${i}`}
          ref={(el) => { refs.current[i] = el; }}
          type="password"
          inputMode="numeric"
          pattern="\d*"
          maxLength={1}
          autoFocus={autoFocus && i === 0}
          disabled={disabled}
          value={digits[i] || ''}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={(e) => handlePaste(i, e)}
          className="w-11 h-14 sm:w-12 sm:h-16 text-center text-2xl font-bold rounded-md bg-[#111111] border-2 border-[#333333] text-white focus:outline-none focus:border-[#22C55E] transition-colors disabled:opacity-60"
          data-testid={`passcode-digit-${i}`}
        />
      ))}
    </div>
  );
}
